import { inject } from '@angular/core';
import { createEffect } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { combineLatest, timer } from 'rxjs';
import { filter, map, startWith } from 'rxjs/operators';
import { DateTime, Duration } from 'luxon';
import { selectDefaultGlobalDateFilterSpan } from '@main/settings/store/specific-setting.selectors';
import { selectGlobalFiltersState } from '@app/store/reducers';
import { applyDefaultGlobalFilters, recomputeRelativeGlobalFilter } from '@app/store/global-filter.actions';
import { DEFAULT_GLOBAL_DATE_FILTER_SPAN_FALLBACK, RELATIVE_FILTER_RECOMPUTE_INTERVAL_MS } from '@app/store/global-filter.reducer';

/**
 * Keeps the global date filter's concrete `startAtOrAfter` aligned with the
 * span that currently governs it:
 *
 * - not customized: the resolved `userInterface.defaultGlobalDateFilterSpan`
 *   setting, applied via `applyDefaultGlobalFilters`;
 * - customized in relative mode: the user's own `relativeSpan`, rolled forward
 *   via `recomputeRelativeGlobalFilter`;
 * - customized in absolute mode: nothing — the user picked exact dates.
 *
 * The setting stream is seeded with the P3M fallback so that relative
 * confirms and resets still take effect while resolved settings have not
 * loaded (or never load); once the real setting arrives the convergence
 * guard absorbs it unless it actually changes the computed bound. Legacy
 * persisted state has no `customized` flag at all; that is treated as
 * "never customized" and is overwritten once with the effective default.
 *
 * The `timer` covers two cases the state/setting sources cannot: a stale
 * window rehydrated at boot, and a midnight rollover in a long-lived tab.
 *
 * The final `filter` is the convergence guard: the actions this effect
 * dispatches change `globalFilters`, which feeds straight back into the
 * `combineLatest`; re-emitting the value the state already holds would loop.
 */
export const maintainGlobalDateFilter = createEffect((store = inject(Store)) => {
  return combineLatest([
    store.pipe(selectDefaultGlobalDateFilterSpan, startWith(DEFAULT_GLOBAL_DATE_FILTER_SPAN_FALLBACK)),
    store.select(selectGlobalFiltersState),
    timer(0, RELATIVE_FILTER_RECOMPUTE_INTERVAL_MS),
  ]).pipe(
    filter(([ , globalFilters ]) => globalFilters != null && !(globalFilters.customized && globalFilters.mode === 'absolute')),
    map(([ settingSpan, globalFilters ]) => {
      const chosenSpan = globalFilters.customized && globalFilters.relativeSpan != null
        ? Duration.fromISO(globalFilters.relativeSpan)
        : null;
      // A customized window with a missing or unparseable span falls back to the setting.
      const span = chosenSpan?.isValid ? chosenSpan : settingSpan;

      return {
        // Calendar math, deliberately not `.as('days')`: P1M means "a month ago", not "30 days ago".
        startAtOrAfter: DateTime.local().minus(span).startOf('day').toString(),
        currentStartAtOrAfter: globalFilters.startAtOrAfter,
        customized: !!globalFilters.customized,
      };
    }),
    filter(({ startAtOrAfter, currentStartAtOrAfter }) => startAtOrAfter !== currentStartAtOrAfter),
    map(({ startAtOrAfter, customized }) => customized
      ? recomputeRelativeGlobalFilter({ startAtOrAfter })
      : applyDefaultGlobalFilters({ startAtOrAfter })),
  );
}, { functional: true });
